/**
 * tooldance.mjs — one reply in the chat, and everything that happened underneath it.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 3.
 *
 * ── THE SENTENCE THE INSTRUMENT EXISTS FOR ────────────────────────────────────────────────────
 * §3: "You see one response from the AI in your chat. Underneath, two tool calls happened, each one
 * a real shell command on a real machine, with each result fed back into the AI's context."
 *
 * That gap is the whole chapter and it is invisible by construction — the product is built to hide
 * it. So the lab has two panes: the chat, which stays empty until the very end and then shows one
 * tidy sentence, and the dance, where seven numbered steps and two shell commands go past. A
 * learner who has watched that once will never again read a single AI reply as a single act.
 * (That sentence said "three" until 2026-08-14; his walkthrough had two, it was raised with him,
 * and he ruled the summary should read two. See RESOLVED below.)
 *
 * ── AND THE SENTENCE THAT CHANGES WHAT IS AT STAKE ────────────────────────────────────────────
 * §4: "In a pure-chat AI, the worst case is a wrong sentence — embarrassing or misleading, but
 * reversible by reading carefully. In a tool-using AI, the worst case is a wrong action —
 * irreversible, real, and consequential."
 *
 * ── NOTHING IS EXECUTED, AND THAT IS NOT A DETAIL ─────────────────────────────────────────────
 * His example commands are real ones — df -h, du -sh, and in §4 delete_file and send_email. This
 * module runs nothing, spawns nothing, and imports nothing that could. A lab about the danger of
 * handing an AI a shell, which handed a browser a shell, would be the joke that writes itself. The
 * gate asserts no execution primitive appears anywhere in the file.
 */

export const SOURCE_3 = 'CodeAI101 module 03 §3 "The tool-call dance — step by step"';
export const SOURCE_4 = 'CodeAI101 module 03 §4 "The new species of failure — and the responsibility shift"';
export const SOURCE_5 = 'CodeAI101 module 03 §5 "Approval, observation, rollback — supervising a tool-equipped AI"';

/* ── A DISCREPANCY THE FOUNDER RESOLVED ────────────────────────────────────────────────────────
 * His summary read "Underneath, THREE tool calls happened" while his own numbered walkthrough
 * contained TWO. Rather than invent a third step to make the number come out, or print a
 * contradiction and let the learner decide which half of the page to distrust, it was raised with
 * him. Ruling, 2026-08-14: "walkthrough is missing a step, fix the summary to two" — so the module
 * now says two and this quotes it whole. The sentence below is his, current, and undisputed.
 * The shelf compiled from that module was recompiled in the same breath. */
export const THE_GAP = 'You see one response from the AI in your chat. Underneath, two tool calls happened, each one a real shell command on a real machine, with each result fed back into the AI\'s context.';
export const RESOLVED = Object.freeze({
  was: 'three', now: 'two',
  ruled_by: 'capt-anil-sharma', ruled_on: '2026-08-14',
  founder_ruling: 'walkthrough is missing a step, fix the summary to two',
  note: 'The module was edited on his instruction — one word, verified as the only change in the file.',
});

export const IN_A_LOOP = 'The AI is operating in a loop, with the tools as its way of touching the world between turns.';
export const THE_SHIFT = 'In a pure-chat AI, the worst case is a wrong sentence — embarrassing or misleading, but reversible by reading carefully. In a tool-using AI, the worst case is a wrong action — irreversible, real, and consequential.';
export const THE_ASK = 'check the disk usage on this server and tell me which directory is the biggest';

/**
 * His seven steps, verbatim and in his order. `actor` says who is moving — and it alternates, which
 * is the shape of the dance: the AI never touches anything, the harness does.
 */
export const STEPS = Object.freeze([
  { n: 1, actor: 'ai', text: 'The AI reads your message in its context, along with the descriptions of the tools it has available.' },
  { n: 2, actor: 'ai', call: { tool: 'run_shell', param: 'command = "df -h"' },
    text: 'The AI decides: I need to run a shell command. It outputs a tool call — run_shell with parameter command = "df -h".' },
  { n: 3, actor: 'harness', runs: 'df -h',
    text: 'The tool harness sees the tool call, runs df -h on the server, captures the output.' },
  { n: 4, actor: 'harness', text: 'The harness sends the output back to the AI as a new context message: "tool result: [the df -h output]."' },
  { n: 5, actor: 'ai', call: { tool: 'run_shell', param: 'command = "du -sh /mnt/storage/ | sort -h | tail -10"' },
    text: 'The AI reads the result. It now knows the disk usage. It decides: I need to find the biggest directory on the busiest disk. It outputs another tool call — run_shell with command = "du -sh /mnt/storage/ | sort -h | tail -10".' },
  { n: 6, actor: 'harness', runs: 'du -sh /mnt/storage/ | sort -h | tail -10',
    text: 'The harness runs the command, captures the output, feeds back.' },
  { n: 7, actor: 'ai', reply: true,
    text: 'The AI reads the output and now produces natural-language reply: "The biggest directory on /mnt/storage is /mnt/storage/projects at 71 GB, mostly from nightly-backups."' },
]);

/** The one line the user sees. His, from step 7 — an example reply, not a measurement.
 *
 * READS "nightly-backups" BECAUSE THE MODULE DOES (2026-08-28). It carried an internal backup-
 * directory name until today; see commit 7503a5c24 for what was renamed.
 * On 2026-08-27 an anonymisation pass renamed the last two internal names in the course SOURCES —
 * codeai101/03-tool-use.md among them — and checked the attested bodies, correctly, finding the
 * names had never reached them. What it did not sweep was CODE, and this lab RENDERS its copy of
 * that sentence to a learner. So the name went on being displayed by the one surface the sweep did
 * not think to look at.
 *
 * gate-tooldance caught it, which is the design working: every string this lab shows must be
 * quoted from the module, so the lab inherits the module's wording — including its redactions —
 * and cannot drift away from it silently. Nothing new was needed to find this; the population of
 * the sweep was the gap, not the guard. */
export const CHAT_REPLY = 'The biggest directory on /mnt/storage is /mnt/storage/projects at 71 GB, mostly from nightly-backups.';

/** §5's three tiers, quoted. */
export const TIERS = Object.freeze([
  { id: 'auto', name: 'Auto-approve (safe-by-design)',
    quote: 'Read operations, search, data-fetching — anything that does not change state. The AI can call these freely; they cost compute but cannot break anything.' },
  { id: 'after', name: 'Approve-after (loud writes)',
    quote: 'Write operations with reversible effects, in low-stakes contexts. The AI calls the tool, the change happens, and the user is shown what was done with the option to undo.' },
  { id: 'before', name: 'Approve-before (gate)',
    quote: 'Operations with irreversible or high-stakes effects — sending emails, making payments, deleting data, running destructive shell commands, deploying to production. The AI proposes the tool call; the human reviews and explicitly approves before the harness runs it.' },
]);
export const CLAUDE_CODE_NOTE = 'Claude Code, for instance, asks before running shell commands that match certain patterns (like rm, git push) and auto-runs the safer ones.';

/** §4's five new failure modes, quoted. Each one is an ACTION going wrong, not a sentence. */
export const FAILURES = Object.freeze([
  { id: 'wrongtool', name: 'Wrong tool', tier: 'before',
    quote: 'The AI decides to use delete_file when it should have used archive_file. The harness obediently runs the wrong tool. Real data is gone. The AI had no way to know this would be wrong; it just generated a plausible-looking tool call.',
    call: { tool: 'delete_file', param: 'path = "/mnt/storage/projects"' }, irreversible: true },
  { id: 'wrongparam', name: 'Wrong parameters', tier: 'before',
    quote: 'The AI calls send_email with the right tool but the wrong to address. The email goes to the wrong person. The mistake is irreversible (you cannot un-send an email).',
    call: { tool: 'send_email', param: 'to = "the wrong person"' }, irreversible: true },
  { id: 'sideeffect', name: 'Unintended side effects', tier: 'after',
    quote: '"Delete all log files older than 7 days" runs cleanly. The side effect: a script that was reading those logs to generate a monthly report now crashes silently.',
    call: { tool: 'run_shell', param: 'command = "delete logs older than 7 days"' }, irreversible: true },
  { id: 'loop', name: 'Loops', tier: 'after',
    quote: 'The AI cannot achieve its goal and keeps trying. Each tool call costs money (compute, API fees) and time. Without limits, an agent can burn through thousands of tool calls and significant cost before someone notices.',
    call: { tool: 'run_shell', param: 'command = "try again"' }, irreversible: false },
  { id: 'escalation', name: 'Permission escalation', tier: 'before',
    quote: 'A tool that was supposed to do one thing turns out to be capable of doing more. The AI discovers this and uses it. Plugin tool can fetch URLs. AI fetches a URL pointing to an internal admin endpoint that should have been protected but was not. AI now has admin access.',
    call: { tool: 'fetch_url', param: 'url = "an internal admin endpoint"' }, irreversible: true },
]);

/**
 * Run the dance to a step, under a supervision tier.
 *
 * The tier only ever GATES — it never changes what the AI proposed. That is the honest shape: §5
 * says "The AI proposes the tool call; the human reviews and explicitly approves before the harness
 * runs it." Supervision does not make the AI wiser, it puts a person between a proposal and an
 * action, and a lab that quietly improved the proposal under a stricter tier would be teaching that
 * approval fixes judgement.
 */
export function danceTo(step, { tier = 'auto', failure = null } = {}) {
  const f = failure ? FAILURES.find((x) => x.id === failure) : null;
  const steps = STEPS.map((s) => {
    /* An injected failure replaces the SECOND tool call — the one the AI decides on after seeing a
       result. That is where §4's examples live: the AI has information and still proposes wrongly. */
    if (f && s.n === 5) return { ...s, call: f.call, failure: f, text: f.quote };
    if (f && s.n === 6) return { ...s, runs: f.call.param, failure: f };
    return s;
  });
  const gatedAt = tier === 'before'
    ? steps.find((s) => s.call && (f ? s.n === 5 : s.n >= 2))?.n ?? null
    : null;
  const reached = gatedAt && step >= gatedAt ? gatedAt : step;
  return {
    steps,
    reached,
    gatedAt,
    /* The chat shows nothing until the final step is reached — the whole point being how much
       happened before one sentence appeared. */
    chatVisible: reached >= 7 && !gatedAt,
    toolCalls: steps.filter((s) => s.call && s.n <= reached).length,
    ranCommands: steps.filter((s) => s.runs && s.n <= reached).length,
    failure: f,
    /* A failure that got past the gate is an ACTION, not a sentence — §4's whole point. */
    actionTaken: Boolean(f && !gatedAt && reached >= 6),
  };
}
